import { Fingerprint, Loader2, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useSupabase } from '@/hooks/useSupabase';
import { supabase } from '@/lib/supabase';

import {
  authPasskey,
  isPasskeySupported,
  listMyPasskeys,
} from './passkeys/api/passkeyService';

/**
 * MfaChallengePage · `/auth/mfa-challenge`
 *
 * Pantalla intermedia entre login (aal1) y la app (aal2). Se llega acá cuando:
 *  - el user tiene rol crítico (superadmin/admin_dendev) con MFA enrolled
 *  - el currentLevel del JWT es 'aal1' (login con password solo, sin TOTP)
 *
 * Flujo:
 *  1. Lista factores TOTP del user
 *  2. Toma el primer factor verified
 *  3. Crea challenge → input de 6 dígitos → verify
 *  4. Si verify OK → session se upgradea a aal2 → redirect a destino original
 *
 * Si no hay factor enrolled, redirige a /settings/security para que lo configure.
 */
export function MfaChallengePage() {
  const { session, loading } = useSupabase();
  const navigate = useNavigate();
  const location = useLocation();
  const from =
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname ??
    '/dashboard';

  const [factorId, setFactorId] = useState<string | null>(null);
  const [factorReady, setFactorReady] = useState(false);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [noFactor, setNoFactor] = useState(false);
  // Passkeys (alternativa a TOTP, no excluyente — ambos se aceptan)
  const [hasPasskeys, setHasPasskeys] = useState(false);
  const [usePasskey, setUsePasskey] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);

  useEffect(() => {
    if (!session) return;
    void (async () => {
      // Paralelo: TOTP factors + passkeys disponibles
      const [factorsRes, passkeys] = await Promise.all([
        supabase.auth.mfa.listFactors(),
        isPasskeySupported() ? listMyPasskeys().catch(() => []) : Promise.resolve([]),
      ]);
      if (factorsRes.error || !factorsRes.data) {
        toast.error('No se pudieron obtener los factores MFA');
        return;
      }
      const verified = factorsRes.data.totp?.find((f) => f.status === 'verified');
      const passkeyCount = passkeys?.length ?? 0;
      if (!verified && passkeyCount === 0) {
        setNoFactor(true);
      } else {
        if (verified) setFactorId(verified.id);
        if (passkeyCount > 0) {
          setHasPasskeys(true);
          setUsePasskey(true); // default: biometría si está disponible
        }
      }
      setFactorReady(true);
    })();
  }, [session]);

  async function onPasskeyAuth() {
    setPasskeyBusy(true);
    try {
      await authPasskey();
      toast.success('Biometría verificada');
      navigate(from, { replace: true });
    } catch (err) {
      const msg = (err as Error).message || '';
      if (msg.includes('NotAllowedError') || msg.includes('cancel')) {
        toast.info('Verificación cancelada');
      } else {
        toast.error('Falló la biometría', { description: msg });
      }
    } finally {
      setPasskeyBusy(false);
    }
  }

  if (loading) {
    return (
      <main className="bg-background flex min-h-screen items-center justify-center">
        <p className="text-fg-muted text-body-lg">Cargando sesión…</p>
      </main>
    );
  }
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // No tiene factor enrolled → debe configurarlo primero
  if (factorReady && noFactor) {
    return (
      <main className="bg-background flex min-h-screen items-center justify-center p-6">
        <Card className="border-border bg-card w-full max-w-sm">
          <CardHeader>
            <CardTitle className="text-title">2FA requerido</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-fg-soft text-body">
              Tu rol requiere autenticación de 2 factores. Configurala primero
              desde Seguridad antes de acceder al panel.
            </p>
            <Button
              className="w-full"
              onClick={() =>
                navigate('/settings/security', { replace: true })
              }
            >
              Configurar 2FA
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!factorId) return;
    if (code.trim().length !== 6) {
      toast.error('Código incompleto', { description: '6 dígitos requeridos' });
      return;
    }
    setSubmitting(true);
    try {
      const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({
        factorId,
      });
      if (chErr || !ch) throw chErr ?? new Error('Challenge falló');

      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: ch.id,
        code: code.trim(),
      });
      if (vErr) throw vErr;

      toast.success('2FA verificado');
      navigate(from, { replace: true });
    } catch (err) {
      toast.error('Código inválido', {
        description: (err as Error).message,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="bg-background flex min-h-screen items-center justify-center p-6">
      <Card className="border-border bg-card w-full max-w-sm">
        <CardHeader className="space-y-1.5">
          <div className="flex items-center gap-2">
            <ShieldCheck className="text-primary h-4 w-4" />
            <CardTitle className="text-title">Verificación 2FA</CardTitle>
          </div>
          <p className="text-fg-muted text-body">
            Ingresá el código de 6 dígitos de tu app autenticadora.
          </p>
        </CardHeader>
        <CardContent>
          {!factorReady ? (
            <div className="flex justify-center py-6">
              <Loader2 className="text-primary h-5 w-5 animate-spin" />
            </div>
          ) : hasPasskeys && usePasskey ? (
            <div className="space-y-4">
              <p className="text-fg-muted text-body">
                Tocá el botón y autorizá con tu biometría (Touch ID, Face ID,
                Windows Hello o huella).
              </p>
              <Button
                type="button"
                className="w-full gap-2"
                onClick={onPasskeyAuth}
                disabled={passkeyBusy}
              >
                {passkeyBusy ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Fingerprint className="size-4" />
                )}
                {passkeyBusy ? 'Esperando biometría…' : 'Entrar con biometría'}
              </Button>
              {factorId && (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => setUsePasskey(false)}
                  disabled={passkeyBusy}
                >
                  Usar código del Authenticator
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                className="text-fg-muted w-full"
                onClick={async () => {
                  await supabase.auth.signOut();
                  navigate('/login', { replace: true });
                }}
                disabled={passkeyBusy}
              >
                Cancelar y salir
              </Button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoFocus
                autoComplete="one-time-code"
                placeholder="123456"
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                className="text-center font-mono text-heading tracking-[0.3em]"
              />
              <Button
                type="submit"
                className="w-full"
                disabled={submitting || code.length !== 6}
              >
                {submitting ? 'Verificando…' : 'Verificar'}
              </Button>
              {hasPasskeys && (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full gap-2"
                  onClick={() => setUsePasskey(true)}
                >
                  <Fingerprint className="size-4" />
                  Usar biometría en su lugar
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={async () => {
                  await supabase.auth.signOut();
                  navigate('/login', { replace: true });
                }}
              >
                Cancelar y salir
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
